import {
  ADMIN_PICKER_SEARCH_MIN_LENGTH,
  ADMIN_PICKER_SOURCE_FILTERS,
  ADMIN_PICKER_STATE_FILTERS,
} from '@carcheck/shared';
import { z } from 'zod';

/**
 * URL filter schema for `/pickers` (AUT-400). Mirrors the backend
 * `ListAdminPickersQuerySchema`: `state` (all | active | disabled), `source`
 * (invite | self_signup | org), `cityCountry` + `city` (picked from the place
 * dictionary), and `q` (name; phone digits too, but only the server decides whether they count — it ignores
 * them for an admin without `view_pii`). Every key degrades to its default
 * on junk, so a hand-edited URL never throws.
 */
export const MIN_SEARCH_LENGTH = ADMIN_PICKER_SEARCH_MIN_LENGTH;

export const pickerFiltersSchema = z.object({
  state: z.enum(ADMIN_PICKER_STATE_FILTERS).catch('all').default('all'),
  source: z.enum(ADMIN_PICKER_SOURCE_FILTERS).optional().catch(undefined),
  // AUT-761 — «lives in»: `city` is the dictionary key, `cityCountry` its ISO code.
  cityCountry: z.string().optional().catch(undefined),
  city: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

export type PickerFilters = z.infer<typeof pickerFiltersSchema>;

export const STATE_OPTIONS = ADMIN_PICKER_STATE_FILTERS;
export const SOURCE_OPTIONS = ADMIN_PICKER_SOURCE_FILTERS;

export const hasActiveFilters = (f: PickerFilters): boolean =>
  f.state !== 'all' ||
  f.source !== undefined ||
  Boolean(f.cityCountry) ||
  Boolean(f.city) ||
  Boolean(f.q);

/** A stable string of the filters: a change resets the cursor stack. */
export const filterKeyOf = (f: PickerFilters): string =>
  [f.state, f.source ?? '', f.cityCountry ?? '', f.city ?? '', f.q ?? ''].join('|');
